import { Assembly, PaintRegion, TableCellStyleEnum } from '@/__generated__/graphql';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { generatePoiDisplayName } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/utils';
import * as state from '@/components/Analysis/state/atoms';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { LocationTag, NOT_APPLICABLE_LABEL, REVIEW_PILL_STATE_MAP } from '@/constants';
import { useFeatureFlag, useAnimationFrame, useRouter } from '@/hooks';
import { primary } from '@/theme/colors';
import { PointOfInterestChecked } from '@/types';
import { Stack, Tooltip, Typography } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useMemo, useCallback, memo } from 'react';
import { usePoiTemplateFields } from '../../hooks/usePoiTemplateFields';
import { ActionMenu } from './ActionMenu';
import { Preview } from './Preview';
import { ReviewAction } from './ReviewAction';
import { ReviewStatus } from './ReviewStatus';
import { Card, Details, Field, Header, StyledCheckbox, Tile } from './styles';
import { Pill } from '@/components/shared/Pill';
import {
  convertTimeStampToDate,
  formatDate,
  formatEnumForDisplay,
  isTimeStamp,
  toKebabCase,
} from '@/utils';
import { usePoiStatusLabels } from '@/hooks/usePoiStatusLabels';
type StatusViewFilterType = 'internal' | 'inspection' | 'both';

interface SummaryCardProps {
  data: PointOfInterestChecked;
  poiIndex?: number;
  variant?: 'default' | 'compact';
  onClick?: (poiId: string | undefined, assembly: Assembly | undefined) => void;
  poiType?: string;
  statusViewFilter?: StatusViewFilterType;
}

export const SummaryCard = memo(function SummaryCard({
  data,
  poiIndex,
  variant = 'default',
  onClick,
  poiType,
  statusViewFilter = 'both',
}: SummaryCardProps) {
  const selectedFieldByType = useAtomValue(state.selectedFieldByType);
  const [selectedReviewPointsOfInterest, setSelectedReviewPointsOfInterest] = useAtom(
    state.selectedReviewPointsOfInterest
  );
  const isAllReviewPointsOfInterestSelected = useAtomValue(
    state.isAllReviewPointsOfInterestSelected
  );
  const selectedReviewPoiTypes = useAtomValue(state.selectedReviewPoiTypes);
  const [checkedGroupedPois, setCheckedGroupedPois] = useAtom(state.checkedGroupedPois);

  const { templateFieldsWithValues, templateOfSelectedPoi } = usePoiTemplateFields({ data });
  const { getPoiStatusLabel } = usePoiStatusLabels();
  const isAITEngineer = useFeatureFlag('ait-engineer');
  const isAPIInspector = useFeatureFlag('api-inspector');
  const canViewReviewScreen = useFeatureFlag(['api-inspector', 'mi-engineer']);

  const poiName = generatePoiDisplayName({
    name: data.name,
    poiId: data.poiId,
    poiType: data.type,
  });

  const router = useRouter();
  const isAtViewerPage = useMemo(() => router.pathname.includes('viewer'), [router.pathname]);

  const selectedField = useMemo(
    () => selectedFieldByType[poiType || 'Other'],
    [selectedFieldByType, poiType]
  );

  const selectedValueFormatted = useMemo(() => {
    if (!data || !selectedField || !(selectedField.value in data)) {
      return NOT_APPLICABLE_LABEL;
    }

    const value = data[selectedField.value as keyof PointOfInterestChecked];

    if (typeof value === 'boolean' || selectedField.value === 'isVerified') {
      return value ? 'Yes' : 'No';
    }

    if (!value) {
      return NOT_APPLICABLE_LABEL;
    }

    if (selectedField.value === 'status') return getPoiStatusLabel(String(value ?? ''), data.type);

    if (selectedField.value === 'accessibility')
      return formatEnumForDisplay(value ?? NOT_APPLICABLE_LABEL);

    if (selectedField.value === 'assembly') return (value as Assembly).tagName;

    if (selectedField.value === 'paintRegion') return (value as PaintRegion).name;

    if (typeof value === 'string' || typeof value === 'number') {
      if (isTimeStamp(value)) {
        const date = convertTimeStampToDate(String(value));
        if (!date || isNaN(date.getTime())) {
          return '';
        }
        return formatDate(date);
      }
    }

    return String(value);
  }, [data, selectedField, getPoiStatusLabel]);

  const isPoiTypeSelected = useMemo(
    () => selectedReviewPoiTypes.includes(poiType || ''),
    [selectedReviewPoiTypes, poiType]
  );

  const isPoiChecked = useMemo(() => !!data.checked, [data.checked]);

  const isPoiSelected = useMemo(() => {
    if (isAtViewerPage) {
      return isPoiChecked;
    }
    return (
      isPoiTypeSelected ||
      isAllReviewPointsOfInterestSelected ||
      selectedReviewPointsOfInterest.includes(data.id || '')
    );
  }, [
    isAtViewerPage,
    isPoiTypeSelected,
    isAllReviewPointsOfInterestSelected,
    selectedReviewPointsOfInterest,
    data.id,
    isPoiChecked,
  ]);

  const handleCheckedPOI = useCallback(
    (value: PointOfInterestChecked, templateId: string) => {
      const updatePois = checkedGroupedPois[templateId]?.map((poi) => ({
        ...poi,
        checked: poi.id === value.id ? !poi.checked : poi.checked,
      }));
      if (updatePois) {
        setCheckedGroupedPois({
          ...checkedGroupedPois,
          [templateId]: updatePois,
        });
      }
    },
    [checkedGroupedPois, setCheckedGroupedPois]
  );

  const handleCheckboxChangeInternal = useCallback(() => {
    if (isAtViewerPage) {
      // Use the handleCheckedPOI logic for viewer page
      const templateId = data.template?.id || data.templateId;
      if (templateId) {
        handleCheckedPOI(data, templateId);
      }
    } else {
      // Use the existing logic for review page
      if (isPoiSelected) {
        setSelectedReviewPointsOfInterest((prev) => prev.filter((id) => id !== data.id));
      } else {
        setSelectedReviewPointsOfInterest((prev) => [...prev, data.id]);
      }
    }
  }, [isAtViewerPage, isPoiSelected, data, setSelectedReviewPointsOfInterest, handleCheckedPOI]);

  const handleCheckboxChange = useAnimationFrame(handleCheckboxChangeInternal);

  const variantLocation = useMemo(
    () => (variant === 'compact' ? LocationTag.ViewerPoiList : LocationTag.ReviewPanelPoiList),
    [variant]
  );

  if (variant === 'compact') {
    return (
      <Tile
        data-testid="summary-tile-component"
        onClick={() => onClick?.(data.id, data.assembly)}
        id="viewer-poi-list-tile"
      >
        <Stack
          direction="row"
          alignItems="center"
          gap="0.8rem"
          sx={{
            flex: 1,
            maxWidth: '50%',
          }}
        >
          <StyledCheckbox
            disableRipple
            checked={isPoiSelected}
            onChange={handleCheckboxChange}
            onClick={(event) => event.stopPropagation()}
            checkboxSize="sm"
            sx={{ margin: 0 }}
            className={`${LocationTag.ViewerPoiList}-${toKebabCase(data.type)}-${
              isPoiChecked ? 'checked' : 'unchecked'
            }`}
          />
          <Tooltip
            title={poiName}
            placement="left"
            arrow
            disableHoverListener={poiName.length < 20}
          >
            <Typography
              variant="caption"
              color="primary.500"
              noWrap
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: 'fit-content',
              }}
            >
              {poiName}
            </Typography>
          </Tooltip>
          {data.isGoverning && (
            <Tooltip title="Governing POI" arrow placement="top">
              <span>
                <AbyssIcon iconName="governing-outline" size="md" />
              </span>
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" gap="1.2rem" alignItems="center">
          <Stack direction="column" gap="0.4rem" alignItems="flex-end">
            <Stack
              direction="row"
              gap="0.8rem"
              alignItems="center"
              sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}
            >
              {isAITEngineer &&
                (statusViewFilter === 'internal' || statusViewFilter === 'both') && (
                  <Stack sx={{ minWidth: 0, maxWidth: '8rem', overflow: 'hidden' }}>
                    <ReviewStatus
                      pointOfInterest={data}
                      templateOfSelectedPoi={templateOfSelectedPoi}
                      location={variantLocation}
                    />
                  </Stack>
                )}
              {canViewReviewScreen &&
                (statusViewFilter === 'inspection' || statusViewFilter === 'both') && (
                  <Stack sx={{ minWidth: 0, maxWidth: '8rem', overflow: 'hidden' }}>
                    <ReviewAction
                      pointOfInterest={data}
                      templateOfSelectedPoi={templateOfSelectedPoi}
                      location={variantLocation}
                    />
                  </Stack>
                )}
              {isAITEngineer && !isAPIInspector && (
                <Stack sx={{ minWidth: 0, maxWidth: '8rem', overflow: 'hidden' }}>
                  <Pill disabled label={selectedValueFormatted} status="linked" state="read" />
                </Stack>
              )}
              {!isAITEngineer && !isAPIInspector && (
                <Stack sx={{ minWidth: 0, maxWidth: '8rem', overflow: 'hidden' }}>
                  <Pill disabled label={selectedValueFormatted} status="linked" state="read" />
                </Stack>
              )}
            </Stack>
          </Stack>
          <ActionMenu pointOfInterest={data} location={variantLocation} />
        </Stack>
      </Tile>
    );
  }

  return (
    <Card selected={isPoiSelected} id="review-panel-poi-list-card">
      <div data-testid="preview-component">
        <Preview data={data} poiIndex={poiIndex} />
      </div>
      <Stack flexGrow="1" gap="0.8rem">
        <Header>
          <Stack
            direction="row"
            alignItems="flex-start"
            gap="1.2rem"
            sx={{
              width: '70%',
              overflow: 'hidden',
              flexGrow: 1,
            }}
          >
            <StyledCheckbox
              // key={isPoiChecked ? 'checked' : 'unchecked'}
              disableRipple
              checked={isPoiSelected}
              onChange={handleCheckboxChange}
              onClick={(event) => event.stopPropagation()}
              checkboxSize="md"
              className={`${LocationTag.ReviewPanelPoiList}-${toKebabCase(data.type)}-${
                isPoiChecked ? 'checked' : 'unchecked'
              }`}
            />
            <Stack direction="row" alignItems="center" gap={1}>
              <Tooltip
                title={poiName}
                placement="left"
                arrow
                disableInteractive={poiName.length < 20}
              >
                <Typography
                  variant="h5"
                  color="primary"
                  fontWeight={600}
                  noWrap
                  sx={{
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flexGrow: 1,
                  }}
                >
                  {poiName}
                </Typography>
              </Tooltip>

              {data.createdByUser?.roleIds?.some((role) => role?.name === 'api-inspector') ? (
                <UserAvatar
                  name={data.createdByUser?.name}
                  picture={data.createdByUser?.picture}
                  sx={{ width: '2.5rem', height: '2.5rem' }}
                />
              ) : (
                <UserAvatar
                  picture="/assets/abyss_fabric_mini_logo_white.svg"
                  sx={{ width: '2.5rem', height: '2.5rem', background: '#000', padding: '0.25rem' }}
                />
              )}
            </Stack>
            {data.isGoverning && (
              <Tooltip title="Governing POI" arrow placement="top">
                <span>
                  <AbyssIcon iconName="governing-outline" />
                </span>
              </Tooltip>
            )}
          </Stack>
          <Stack direction="row" gap="1.2rem" alignItems="center">
            {isAITEngineer && (
              <ReviewStatus
                pointOfInterest={data}
                templateOfSelectedPoi={templateOfSelectedPoi}
                location={variantLocation}
              />
            )}
            {canViewReviewScreen && (
              <ReviewAction
                pointOfInterest={data}
                templateOfSelectedPoi={templateOfSelectedPoi}
                location={variantLocation}
              />
            )}
            <ActionMenu pointOfInterest={data} location={variantLocation} />
          </Stack>
        </Header>
        <Details
          data-testid="details-component"
          columnCount={templateFieldsWithValues.length > 4 ? 2 : 1}
        >
          {templateFieldsWithValues.map((field) => (
            <Field key={field.label}>
              <Typography lineHeight="2.4rem" variant="h6" fontWeight={400} color={primary.gray}>
                {field.label}:
              </Typography>
              {field.displayStyle !== TableCellStyleEnum.Chip ? (
                <Stack direction="row" alignItems="center" gap="0.4rem">
                  <Typography lineHeight="2.4rem" variant="h6" color={primary.independence}>
                    {field.value}
                  </Typography>
                  {field.valueIcon && <AbyssIcon size="md" iconName={field.valueIcon} />}
                </Stack>
              ) : (
                <Pill
                  size="small"
                  label={field.value}
                  disabled
                  state="default"
                  status={REVIEW_PILL_STATE_MAP[field.value]}
                />
              )}
            </Field>
          ))}
        </Details>
      </Stack>
    </Card>
  );
});
